/* eslint-disable react/jsx-props-no-spreading */
import {
  Box, Chip, ChipProps, Tooltip,
} from '@mui/material';
import { LICENSE_TYPE } from '@/consts/common';
import { getLicenseColors } from '@/consts/licenseColors';
import { getProductLabel } from '@/consts/licenseManagement';
import { getLicenseLabel } from '@/utils/helpers/userManagementHelper';
import { ILicense } from '@/services/licenses/types';
import { toDateDisplay } from '@/utils/helpers/dateHelpers';

interface LicenseChipProps extends Omit<ChipProps, 'label' | 'color'> {
  license?: Pick<ILicense, 'licenseType' | 'product' | 'expiresAt'> | null;
  showProductTooltip?: boolean;
  showExpiresAt?: boolean;
}

const LicenseChip = ({
  license = null,
  showProductTooltip = true,
  showExpiresAt = false,
  ...chipProps
}: LicenseChipProps) => {
  const licenseType = license?.licenseType ?? LICENSE_TYPE.UNLICENSED;
  const { bgColor, textColor } = getLicenseColors(licenseType);
  const licenseLabel = getLicenseLabel(licenseType);

  const chip = (
    <Chip
      {...chipProps}
      label={licenseLabel}
      size="small"
      sx={{
        backgroundColor: bgColor,
        color: textColor,
        fontWeight: 500,
        width: 100,
        borderRadius: '6px',
        textTransform: 'capitalize',
      }}
    />
  );

  const shouldShowProductTooltip = Boolean(
    license && showProductTooltip && licenseType !== LICENSE_TYPE.UNLICENSED,
  );

  if (!shouldShowProductTooltip || !license) {
    return chip;
  }

  const { product, expiresAt } = license;

  return (
    <Tooltip
      title={(
        <Box sx={{ textAlign: 'center' }}>
          {getProductLabel(product)}
          {showExpiresAt && (
          <>
            <br />
            {`Expires at: ${toDateDisplay(expiresAt)}`}
          </>
          )}
        </Box>
      )}
      arrow
      enterTouchDelay={0}
      leaveTouchDelay={3000}
    >
      <span>{chip}</span>
    </Tooltip>
  );
};

export default LicenseChip;
